import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Building2, 
  User, 
  LogOut, 
  X, 
  Plus,
  Users as UsersIcon,
  BarChart3,
  Database,
  Loader2,
  Menu,
  Search
} from 'lucide-react';
import { useAuth, useLogout, useAuthCheck } from '../hooks/useAuth';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { isLoading: authLoading } = useAuthCheck();
  const logoutMutation = useLogout();

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      // Erro já tratado pelo hook
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const getMenuItems = () => {
    if (!user || !isAuthenticated) {
      return [];
    }

    if (user.tipo === 'cliente') {
      return [
        {
          path: '/my-recommendations',
          label: 'Minhas Recomendações',
          icon: Building2,
          description: 'Propriedades personalizadas'
        },
        {
          path: '/catalog',
          label: 'Catálogo Completo',
          icon: Search,
          description: 'Todos os imóveis disponíveis'
        },
        {
          path: '/profile',
          label: 'Meu Perfil',
          icon: User,
          description: 'Gerenciar dados pessoais'
        }
      ];
    } else if (user.tipo === 'concessionaria') {
      return [
        {
          path: '/dashboard',
          label: 'Dashboard',
          icon: BarChart3,
          description: 'Visão geral do sistema'
        },
        {
          path: '/properties',
          label: 'Gestão de Imóveis',
          icon: Building2,
          description: 'Gerenciar propriedades'
        },
        {
          path: '/properties/add',
          label: 'Adicionar Imóvel',
          icon: Plus,
          description: 'Cadastrar nova propriedade'
        },
        {
          path: '/clients',
          label: 'Gestão de Clientes',
          icon: UsersIcon,
          description: 'Gerenciar clientes'
        },
        {
          path: '/profile',
          label: 'Meu Perfil',
          icon: User,
          description: 'Gerenciar dados pessoais'
        }
      ];
    }

    return [];
  };

  const menuItems = getMenuItems();

  // Se está carregando, mostrar loading
  if (authLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 lg:relative lg:bg-transparent">
        <div className="flex flex-col items-center space-y-4 bg-gray-800 p-8 rounded-lg">
          <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
          <p className="text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se não está autenticado, não mostrar sidebar
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Mobile Toggle Button */}
      <button
        onClick={onToggle}
        className="fixed top-4 left-4 z-50 p-2 bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-all duration-200 border border-gray-600 lg:hidden"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full w-64 bg-gray-800 border-r border-gray-600 z-50 
        transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:z-auto lg:flex-shrink-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-600">
            <Link 
              to={user?.tipo === 'cliente' ? '/my-recommendations' : '/dashboard'} 
              className="flex items-center space-x-3"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                {user?.tipo === 'cliente' ? (
                  <Building2 className="w-5 h-5 text-white" />
                ) : (
                  <Database className="w-5 h-5 text-white" />
                )}
              </div>
              <span className="text-xl font-bold text-white">
                {user?.tipo === 'cliente' ? 'Catálogo' : 'Gestão'}
              </span>
            </Link>
            <button
              onClick={onToggle}
              className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-6 border-b border-gray-600">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.nome || 'Usuário'}</p>
                <p className="text-gray-400 text-sm capitalize">{user?.tipo || 'Tipo'}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400 text-sm">Nenhum item de menu disponível</p>
                <p className="text-gray-500 text-xs mt-2">Tipo: {user?.tipo}</p>
              </div>
            ) : (
              menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        onToggle();
                      }
                    }}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
                      isActive(item.path)
                        ? 'bg-red-500 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.label}</p>
                      <p className="text-xs opacity-75 truncate">{item.description}</p>
                    </div>
                  </Link>
                );
              })
            )}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-600">
            <button
              onClick={handleLogout}
              disabled={logoutMutation.isLoading}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-200"
            >
              {logoutMutation.isLoading ? (
                <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <LogOut className="w-4 h-4" />
              )}
              <span className="font-medium">Sair</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
